/**
 * SAML Error Page
 * Visar felmeddelande när SAML-autentisering misslyckas
 */

import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTranslation } from '@/store/languageStore';

const SamlError: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  usePageTitle(t('auth.samlErrorTitle'));

  const errorMessage = searchParams.get('message') || t('auth.samlError') || 'SAML-autentisering misslyckades';
  const organizationId = searchParams.get('organizationId');

  const handleRetry = () => {
    if (organizationId) {
      // Försök SAML-inloggning igen
      window.location.href = `/api/auth/saml/${organizationId}/initiate`;
    } else {
      // Gå tillbaka till landing
      navigate('/auth/landing');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-100">
      <div className="max-w-md w-full bg-white dark:bg-white rounded-lg shadow-md p-8">
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-800 mb-2">
            {t('auth.samlErrorTitle') || 'Inloggning misslyckades'}
          </h2>
          <p className="text-textSecondary dark:text-textSecondary">{errorMessage}</p>
        </div>

        <div className="space-y-3">
          {organizationId && (
            <button
              onClick={handleRetry}
              className="w-full bg-blue-500 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-600 transition duration-200"
            >
              {t('auth.retryLogin') || 'Försök igen'}
            </button>
          )}
          <button
            onClick={() => navigate('/auth/landing')}
            className="w-full bg-gray-500 text-white font-bold py-2 px-4 rounded-md hover:bg-gray-600 transition duration-200"
          >
            {t('auth.backToLanding') || 'Tillbaka till startsidan'}
          </button>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-gray-300 dark:bg-gray-300 text-gray-700 dark:text-gray-700 font-bold py-2 px-4 rounded-md hover:bg-gray-400 dark:hover:bg-gray-400 transition duration-200"
          >
            {t('auth.goToLogin') || 'Gå till inloggning'}
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-200">
          <p className="text-sm text-gray-600 text-center">
            {t('auth.contactSupport') || 'Om problemet kvarstår, kontakta support.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SamlError;

